import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {useState} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, Button, IconButton} from '@primer/react'
import type {Repository} from '@github-ui/current-repository'
import {NewAgentTaskForm} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {TOGGLE_COPILOT_OVERLAY} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import {useCopilotChatPayload} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayload'

export type NewAgentTaskButtonProps = {
  isIconButton?: boolean
  currentRepo?: Repository
  filePath?: string
  hideRepoSelector?: boolean
  hideIcon?: boolean
  eventType: string
  size?: 'small' | 'medium' | 'large'
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
}

export {NewAgentTaskButton, NewAgentTaskActionListItem}

function NewAgentTaskButton({
  isIconButton,
  currentRepo,
  filePath,
  hideRepoSelector,
  eventType,
  size,
  tooltipDirection,
}: NewAgentTaskButtonProps) {
  const [open, setOpen] = useState(false)
  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => {
        return isIconButton ? (
          <IconButton
            icon={AgentIcon}
            aria-label="New agent task"
            // We shouldn't need this but the typing requires something
            aria-labelledby={undefined}
            size={size}
            tooltipDirection={tooltipDirection}
            {...props}
          />
        ) : (
          <Button {...props} leadingVisual={<AgentIcon />} size={size}>
            New agent task
          </Button>
        )
      }}
    >
      <ConsecutiveCAPI401sProvider>
        <NewAgentTaskButtonContent
          currentRepo={currentRepo}
          filePath={filePath}
          hideRepoSelector={hideRepoSelector}
          setOpen={setOpen}
          eventType={eventType}
        />
      </ConsecutiveCAPI401sProvider>
    </AnchoredOverlay>
  )
}

function NewAgentTaskActionListItem({
  currentRepo,
  filePath,
  eventType,
  ...rest
}: Omit<NewAgentTaskButtonProps, 'isActionListItem' | 'isIconButton' | 'size' | 'tooltipDirection'> &
  React.ComponentProps<typeof ActionList.Item>) {
  const [open, setOpen] = useState(false)
  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => (
        <ActionList.Item {...props} {...rest}>
          <span>New agent task</span>
        </ActionList.Item>
      )}
    >
      <ConsecutiveCAPI401sProvider>
        <NewAgentTaskButtonContent
          currentRepo={currentRepo}
          filePath={filePath}
          setOpen={setOpen}
          eventType={eventType}
        />
      </ConsecutiveCAPI401sProvider>
    </AnchoredOverlay>
  )
}

function NewAgentTaskButtonContent({
  currentRepo,
  filePath,
  hideRepoSelector,
  setOpen,
  eventType,
}: {
  currentRepo?: Repository
  filePath?: string
  hideRepoSelector?: boolean
  setOpen: (open: boolean) => void
  eventType: string
}) {
  // Once this PR (https://github.com/github/github/pull/392100) merges, we'll be able to remove this as it's duplicative of what's already in
  // NewAgentTaskForm. We just need it here in this component now to pass an apiURL into useTasks below, but we should move to just using getEnv().copilotApiOverrideUrl
  // inside useTasks in the future.
  const copilotChatPayload = useCopilotChatPayload()
  // Warm up the cache by prefetching tasks
  // TODO: Consider using something inside useCreateTask to prefetch tasks instead
  // e.g await queryClient.refetchQueries({ queryKey: ['posts'], type: 'active' })
  useTasks({apiURL: copilotChatPayload?.apiURL ?? '', sessionsPollingInterval: false})
  const {createTask} = useCreateTask({
    apiURL: copilotChatPayload?.apiURL ?? '',
  })
  return (
    <NewAgentTaskForm
      createTask={createTask}
      currentRepo={currentRepo}
      filePath={filePath}
      onCreate={() => {
        setOpen(false)
        document.dispatchEvent(new CustomEvent(TOGGLE_COPILOT_OVERLAY))
      }}
      hideRepoSelector={hideRepoSelector}
      eventType={eventType}
    />
  )
}

try{ NewAgentTaskButton.displayName ||= 'NewAgentTaskButton' } catch {}
try{ NewAgentTaskActionListItem.displayName ||= 'NewAgentTaskActionListItem' } catch {}
try{ NewAgentTaskButtonContent.displayName ||= 'NewAgentTaskButtonContent' } catch {}